import { failure, success } from '../types.js';
import type { PlainDate, Result } from '../types.js';

const DAY_MS = 86_400_000;
export type Instant = number | string;

export function parsePlainDate(value: string): Result<PlainDate> {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000'))
    return failure('invalid_date');
  const timestamp = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(timestamp) || new Date(timestamp).toISOString().slice(0, 10) !== value)
    return failure('invalid_date');
  return success(value as PlainDate);
}

export function instantMilliseconds(instant: Instant): Result<number> {
  if (typeof instant === 'number')
    return Number.isSafeInteger(instant) && Math.abs(instant) <= 8_640_000_000_000_000
      ? success(instant)
      : failure('invalid_instant');
  if (
    !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?Z$/.test(instant) ||
    !parsePlainDate(instant.slice(0, 10)).ok
  )
    return failure('invalid_instant');
  const timestamp = Date.parse(instant);
  return Number.isFinite(timestamp) ? success(timestamp) : failure('invalid_instant');
}

export function addDays(date: string, delta: number): Result<PlainDate> {
  const parsed = parsePlainDate(date);
  if (!parsed.ok) return parsed;
  if (!Number.isSafeInteger(delta)) return failure('invalid_day_offset');
  const timestamp = Date.parse(`${date}T00:00:00.000Z`) + delta * DAY_MS;
  if (!Number.isSafeInteger(timestamp) || Math.abs(timestamp) > 8_640_000_000_000_000)
    return failure('invalid_day_offset');
  return parsePlainDate(new Date(timestamp).toISOString().slice(0, 10));
}

/**
 * The first day of the last `days` days, counting today: the last 30 days of `2026-10-19` start on
 * `2026-09-20`. Calendar days, never shifted by a zone. Roles & lists' "· 30 days" counts and the
 * Audit log's "Last 30 days" both start here (APP-148 R97), so a count opens exactly what it counts.
 */
export function lastDaysFirstDay(today: string, days: number): PlainDate {
  const first = addDays(today, 1 - days);
  if (!first.ok) throw new Error(`no day ${String(days)} days before ${today}`);
  return first.value;
}

/** The instant the Audit log starts a day at: midnight UTC, as its times read (APP-044 §33). */
export function auditDayStart(day: string): string {
  return `${day}T00:00:00.000Z`;
}

// ---- The last N months (APP-199) ------------------------------------------------------------

/** How many months "the last 12 months" holds. */
export const LAST_MONTHS = 12;

/** A window of whole calendar months ending today. */
export interface MonthsWindow {
  /** The 1st of its first month: `2025-11-01` for any day of October 2026. */
  readonly from: PlainDate;
  /** Today: the last day it counts. */
  readonly to: PlainDate;
  /** Its calendar months, oldest first, today's last: `2025-11` … `2026-10`. */
  readonly months: readonly string[];
}

const MONTH_LIMIT = 1200;

/**
 * "The last N months" (APP-199, Tom 2026-10-27): whole calendar months, from the 1st of the month
 * N − 1 months back, to today. Today's month is one of them, however few of its days have gone, so
 * on Oct 27, 2026 the last 12 months run from Nov 1, 2025, and on Nov 1, 2026 from Dec 1, 2025.
 * `today` is the organization's date (`lastMonthsWindowAt` works it out from an instant). Every
 * report reads its window here: Insights' Overview, the heat map, Team recognition, All awards,
 * the Repeat flag (the repeat rule's months, here N), Home's team snapshot, the approval and entry
 * pages' recent recognition, Ask the data and the assistant's recognition answers.
 */
export function lastMonths(today: string, months: number = LAST_MONTHS): Result<MonthsWindow> {
  const parsed = parsePlainDate(today);
  if (!parsed.ok) return parsed;
  if (!Number.isSafeInteger(months) || months < 1 || months > MONTH_LIMIT)
    return failure('invalid_month_count', 'months');
  const last = Number(today.slice(0, 4)) * 12 + Number(today.slice(5, 7)) - 1;
  const first = last - (months - 1);
  if (first < 12) return failure('invalid_date_range');
  const month = (n: number) =>
    `${String(Math.floor(n / 12)).padStart(4, '0')}-${String((n % 12) + 1).padStart(2, '0')}`;
  const list = Array.from({ length: months }, (_, i) => month(first + i));
  return success({ from: `${month(first)}-01` as PlainDate, to: parsed.value, months: list });
}

/** `lastMonths` for a date the caller already holds as valid; throws on anything else. */
export function lastMonthsWindow(today: string, months: number = LAST_MONTHS): MonthsWindow {
  const window = lastMonths(today, months);
  if (!window.ok)
    throw new Error(
      `no last ${String(months)} months for ${today}: ${window.issues[0]?.code ?? ''}`,
    );
  return window.value;
}

/** The last N months as of an instant, by the organization's calendar date then. */
export function lastMonthsWindowAt(
  now: Instant,
  timeZone: string,
  months: number = LAST_MONTHS,
): Result<MonthsWindow> {
  const today = dateInZone(now, timeZone);
  return today.ok ? lastMonths(today.value, months) : today;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Where a window starts, in a scope line: "since Nov 1, 2025" (APP-199). One wording for the web,
 * Ask the data and Teams, so every reading of the last 12 months names the same day.
 */
export function sinceWords(from: string): string {
  const name = MONTH_NAMES[Number(from.slice(5, 7)) - 1] ?? from.slice(5, 7);
  return `since ${name} ${String(Number(from.slice(8, 10)))}, ${String(Number(from.slice(0, 4)))}`;
}

export function previousBusinessDay(date: string): Result<PlainDate> {
  const parsed = parsePlainDate(date);
  if (!parsed.ok) return parsed;
  const weekday = new Date(`${date}T00:00:00.000Z`).getUTCDay();
  return addDays(date, weekday === 0 ? -2 : weekday === 6 ? -1 : 0);
}

/**
 * The date `days` business days after `date`, Monday to Friday (no holidays: the organization's
 * business days are weekdays, as `previousBusinessDay` reads them). From a weekend day it counts
 * from the Monday after. APP-245 D2: a Spot letter's hand-out reminder.
 */
export function addBusinessDays(date: string, days: number): Result<PlainDate> {
  const parsed = parsePlainDate(date);
  if (!parsed.ok) return parsed;
  let day: PlainDate = parsed.value;
  for (let left = days; left > 0;) {
    const next = addDays(day, 1);
    if (!next.ok) return next;
    day = next.value;
    const weekday = new Date(`${day}T00:00:00.000Z`).getUTCDay();
    if (weekday !== 0 && weekday !== 6) left -= 1;
  }
  return success(day);
}

function formatter(timeZone: string): Result<Intl.DateTimeFormat> {
  try {
    return success(
      new Intl.DateTimeFormat('en-CA', {
        timeZone,
        calendar: 'gregory',
        numberingSystem: 'latn',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      }),
    );
  } catch {
    return failure('invalid_time_zone');
  }
}

function wallTime(timestamp: number, format: Intl.DateTimeFormat): string {
  const parts = new Map(format.formatToParts(timestamp).map((part) => [part.type, part.value]));
  const year = (parts.get('year') ?? '').padStart(4, '0');
  return `${year}-${parts.get('month') ?? ''}-${parts.get('day') ?? ''}T${parts.get('hour') ?? ''}:${parts.get('minute') ?? ''}:${parts.get('second') ?? ''}`;
}

export function dateInZone(instant: Instant, timeZone: string): Result<PlainDate> {
  const timestamp = instantMilliseconds(instant);
  if (!timestamp.ok) return timestamp;
  const format = formatter(timeZone);
  return format.ok ? parsePlainDate(wallTime(timestamp.value, format.value).slice(0, 10)) : format;
}

/** Uses actual timezone offsets around the requested date, including repeated/skipped DST times. */
export function localDateTime(
  date: string,
  time: string,
  timeZone: string,
  occurrence: 'earlier' | 'later' = 'later',
): Result<number> {
  const parsed = parsePlainDate(date);
  if (!parsed.ok) return parsed;
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(time)) return failure('invalid_local_time');
  const format = formatter(timeZone);
  if (!format.ok) return format;
  const targetWall = `${date}T${time}`;
  const targetAsUtc = Date.parse(`${targetWall}Z`);
  const offsets = new Set<number>();
  for (let hours = -36; hours <= 36; hours += 6) {
    const probe = targetAsUtc + hours * 3_600_000;
    offsets.add(Date.parse(`${wallTime(probe, format.value)}Z`) - probe);
  }
  const candidates = [...offsets]
    .map((offset) => targetAsUtc - offset)
    .filter((candidate) => wallTime(candidate, format.value) === targetWall);
  if (!candidates.length) return failure('nonexistent_local_time');
  return success(occurrence === 'earlier' ? Math.min(...candidates) : Math.max(...candidates));
}

export function daysLeft(now: Instant, deadlineDate: string, timeZone: string): Result<number> {
  const today = dateInZone(now, timeZone);
  if (!today.ok) return today;
  const deadline = parsePlainDate(deadlineDate);
  if (!deadline.ok) return deadline;
  return success(
    (Date.parse(`${deadline.value}T00:00:00Z`) - Date.parse(`${today.value}T00:00:00Z`)) / DAY_MS,
  );
}

/** First and last real instants of an IANA calendar day. Midnight or the last
 * wall-clock hour can be skipped, and a dateline change can skip the entire date. */
export function localDayBounds(
  date: string,
  timeZone: string,
): Result<{ readonly start: number; readonly end: number }> {
  const parsed = parsePlainDate(date);
  if (!parsed.ok) return parsed;
  const format = formatter(timeZone);
  if (!format.ok) return format;
  const midnightUtc = Date.parse(`${date}T00:00:00Z`),
    hour = 3_600_000;
  const belongs = (timestamp: number): boolean =>
    wallTime(timestamp, format.value).slice(0, 10) === date;
  let first: number | undefined, last: number | undefined;
  for (let offset = -36; offset <= 36; offset++) {
    const probe = midnightUtc + offset * hour;
    if (belongs(probe)) {
      first ??= probe;
      last = probe;
    }
  }
  if (first === undefined || last === undefined) return failure('nonexistent_local_date');
  let before = first - hour,
    inside = first;
  while (inside - before > 1) {
    const middle = before + Math.floor((inside - before) / 2);
    if (belongs(middle)) inside = middle;
    else before = middle;
  }
  const start = inside;
  inside = last;
  let after = last + hour;
  while (after - inside > 1) {
    const middle = inside + Math.floor((after - inside) / 2);
    if (belongs(middle)) inside = middle;
    else after = middle;
  }
  return success({ start, end: inside });
}
